import { api } from '@/lib/api';
import type { Account } from '@/lib/api';

export interface TaxRate {
  tax_rate_id: string;
  tax_type: string;
  name: string;
  tax_rate: number;
  status: string;
  effective_rate: number;
  display_tax_rate: string;
  can_apply_to_asset: boolean;
  can_apply_to_equity: boolean;
  can_apply_to_expense: boolean;
  can_apply_to_liability: boolean;
  can_apply_to_revenue: boolean;
}
// Import enums that are used as values directly, and others as types
import {
  EntityType,     // Potentially used for casting/validation if not just type
  EntityStatus    // Potentially used for casting/validation if not just type
} from '@/types/entity.types';
import type {
  EntitySummary,
  EntityResponse,
  EntityCreate,
  EntityUpdate,
  EntityListResponse,
  EntityDashboardData,
  EntityHealthMetrics,
  ConnectionTestResult,
  SyncStatus,
  SyncTriggerResponse,
  SyncJobStatus,
  SyncConflictError,
  OAuthInitiateResponse,
  OAuthCallbackData,
  CompleteConnectionResponse,
  EntityFilters,
  EntityEnums,
  CreateEntityResponse,
  UpdateEntityResponse,
  DeleteEntityResponse,
  EntityWizardStep1,
  EntityWizardStep2,
  EntityWizardStep3,
  // Tag-related imports
  TagResponse,
  TagCreateRequest,
  TagUpdateRequest,
  TagListResponse,
  TagDeleteResponse,
  TagFilters,
  TagBulkUpdateRequest,
  TagBulkUpdateResponse,
  PaginationInfo
  // ConnectionStatus removed from here as it's now a direct import
} from '@/types/entity.types';
// Import contact and audit types
import type {
  ContactFilters,
  ContactListResponse,
  ContactDetailResponse
} from '@/types/contact.types';
import type {
  AuditLogFilters,
  AuditLogListResponse,
  AuditLogDetailResponse,
  AuditLogStatsResponse
} from '@/types/audit.types';

export interface EntityConnectionStatus {
  entity_id: string;
  entity_name: string;
  type: string;
  connection_status: {
    status: string;
    last_checked?: string;
    error_message?: string;
  };
}

// Define a type for the raw entity structure from the backend
interface RawBackendEntity {
  entity_id: string;
  entity_name: string;
  type: string; // Or specific EntityType if backend guarantees it
  status: string; // Or specific EntityStatus
  connection_details?: {
    status?: string;
    last_sync?: string;
    error_message?: string;
    // other fields if present
  };
  // Include sync status information if available from backend
  sync_status?: {
    is_syncing?: boolean;
    current_step?: string;
    progress_percentage?: number;
    estimated_remaining?: string;
    user_message?: string;
    last_sync_completed?: string;
    sync_duration_warning?: string;
  };
  // Enhanced sync job tracking
  active_sync_job_id?: string;
  sync_job_status?: {
    sync_job_id?: string;
    entity_id?: string;
    status?: 'processing' | 'completed' | 'failed';
    progress_percent?: number;
    stage?: 'bills' | 'attachments' | 'completion';
    started_at?: string;
    error_details?: Record<string, any>;
    duration_seconds?: number;
  };
  // Include other top-level fields the backend might send for EntitySummary
  pending_items_count?: number;
  error_count?: number;
  health_score?: number;
  // Tag details from backend (snake_case)
  tag_details?: Array<{
    tag_id: string;
    name: string;
    color?: string;
    description?: string;
  }>;
  // Any other fields like created_at, updated_at, xero_tenant_id etc. if needed elsewhere,
  // but EntitySummary doesn't strictly require them.
}

interface RawBackendResponse {
  client_id: string;
  entities: RawBackendEntity[];
  total: number;
  credit_info?: {
    credit_balance: number;
    credits_used_total: number;
    credits_used_llm: number;
    last_credit_usage?: string;
  };
  // Add pagination fields if your backend list endpoint for entities returns them
  // pagination?: { current_page: number; page_size: number; total_items: number; total_pages: number; };
}

export class EntitiesService {
  // Request deduplication cache to prevent duplicate API calls
  private static requestCache = new Map<string, Promise<any>>();

  // Active AbortControllers for request cancellation
  private static abortControllers = new Map<string, AbortController>();

  /**
   * Create or get cached request with AbortController support
   */
  private static async getOrCreateRequest<T>(
    cacheKey: string,
    requestFactory: (signal: AbortSignal) => Promise<T>,
    timeoutMs: number = 30000
  ): Promise<T> {
    // Check if request is already in flight
    const existingRequest = this.requestCache.get(cacheKey);
    if (existingRequest) {
      console.debug(`Deduplicating request: ${cacheKey}`);
      return existingRequest;
    }

    // Cancel previous request for the same key if exists
    const existingController = this.abortControllers.get(cacheKey);
    if (existingController) {
      existingController.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    this.abortControllers.set(cacheKey, controller);

    // Set up timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
      console.warn(`Request timeout for: ${cacheKey}`);
    }, timeoutMs);

    // Create and cache the request
    const requestPromise = requestFactory(controller.signal)
      .finally(() => {
        // Cleanup on completion
        clearTimeout(timeoutId);
        this.requestCache.delete(cacheKey);
        this.abortControllers.delete(cacheKey);
      });

    this.requestCache.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Cancel all pending requests (useful for component unmount)
   */
  private static cancelAllRequests(): void {
    this.abortControllers.forEach((controller, key) => {
      controller.abort();
      console.debug(`Cancelled request: ${key}`);
    });
    this.abortControllers.clear();
    this.requestCache.clear();
  }
  /**
   * Validate and safely cast EntityType with fallback
   */
  private static validateEntityType(type: string): EntityType {
    const validTypes = Object.values(EntityType) as string[];
    if (validTypes.includes(type)) {
      return type as EntityType;
    }
    console.warn(`Invalid entity type received: ${type}, defaulting to MANUAL`);
    return EntityType.MANUAL;
  }

  /**
   * Validate and safely cast EntityStatus with fallback
   */
  private static validateEntityStatus(status: string): EntityStatus {
    const validStatuses = Object.values(EntityStatus) as string[];
    if (validStatuses.includes(status)) {
      return status as EntityStatus;
    }
    console.warn(`Invalid entity status received: ${status}, defaulting to INACTIVE`);
    return EntityStatus.INACTIVE;
  }

  /**
   * Validate sync job status with type safety
   */
  private static validateSyncJobStatus(status: string): 'processing' | 'completed' | 'failed' {
    const validStatuses: readonly string[] = ['processing', 'completed', 'failed'];
    if (validStatuses.includes(status)) {
      return status as 'processing' | 'completed' | 'failed';
    }
    console.warn(`Invalid sync job status received: ${status}, defaulting to processing`);
    return 'processing';
  }

  /**
   * Validate sync job stage with type safety
   */
  private static validateSyncJobStage(stage: string): 'bills' | 'attachments' | 'completion' {
    const validStages: readonly string[] = ['bills', 'attachments', 'completion'];
    if (validStages.includes(stage)) {
      return stage as 'bills' | 'attachments' | 'completion';
    }
    console.warn(`Invalid sync job stage received: ${stage}, defaulting to bills`);
    return 'bills';
  }

  /**
   * Transform raw backend entity to EntitySummary (internal utility)
   */
  private static transformRawEntity(rawEntity: RawBackendEntity): EntitySummary {
    let mappedConnectionStatus: EntityStatus = EntityStatus.DISCONNECTED; // Default
    const backendConnStatus = rawEntity.connection_details?.status;

    if (backendConnStatus) {
      if (backendConnStatus.toLowerCase() === 'active') {
        mappedConnectionStatus = EntityStatus.ACTIVE;
      } else if (backendConnStatus.toLowerCase() === 'disconnected') {
        mappedConnectionStatus = EntityStatus.DISCONNECTED;
      } else if (backendConnStatus.toLowerCase() === 'error') {
        mappedConnectionStatus = EntityStatus.ERROR;
      } else if (backendConnStatus.toLowerCase() === 'pending') {
        mappedConnectionStatus = EntityStatus.PENDING;
      } else if (backendConnStatus.toLowerCase() === 'inactive') {
        mappedConnectionStatus = EntityStatus.INACTIVE;
      } else if (backendConnStatus.toLowerCase() === 'syncing') {
        mappedConnectionStatus = EntityStatus.SYNCING;
      }
    }
    
    // If sync status indicates syncing, override connection status
    if (rawEntity.sync_status?.is_syncing) {
      mappedConnectionStatus = EntityStatus.SYNCING;
    }
    
    return {
      entity_id: rawEntity.entity_id,
      entity_name: rawEntity.entity_name,
      type: this.validateEntityType(rawEntity.type),
      status: this.validateEntityStatus(rawEntity.status),
      connection_status: mappedConnectionStatus,
      last_sync: rawEntity.connection_details?.last_sync,
      error_message: rawEntity.connection_details?.error_message,
      pending_items_count: rawEntity.pending_items_count,
      error_count: rawEntity.error_count,
      health_score: rawEntity.health_score,
      sync_status: rawEntity.sync_status ? {
        is_syncing: rawEntity.sync_status.is_syncing || false,
        current_step: rawEntity.sync_status.current_step,
        progress_percentage: rawEntity.sync_status.progress_percentage,
        user_message: rawEntity.sync_status.user_message,
        estimated_remaining: rawEntity.sync_status.estimated_remaining,
        last_sync_completed: rawEntity.sync_status.last_sync_completed,
        sync_duration_warning: rawEntity.sync_status.sync_duration_warning,
      } : undefined,
      // Transform tag details from backend snake_case to frontend camelCase
      tagDetails: rawEntity.tag_details?.map(tag => ({
        tagId: tag.tag_id,
        name: tag.name,
        color: tag.color || '#6b7280',
        description: tag.description
      })) || [],
      // Transform enhanced sync job tracking with type safety
      activeSyncJobId: rawEntity.active_sync_job_id,
      syncJobStatus: rawEntity.sync_job_status ? {
        syncJobId: rawEntity.sync_job_status.sync_job_id || '',
        entityId: rawEntity.sync_job_status.entity_id || rawEntity.entity_id,
        status: rawEntity.sync_job_status.status
          ? this.validateSyncJobStatus(rawEntity.sync_job_status.status)
          : 'processing',
        progressPercent: Math.max(0, Math.min(100, rawEntity.sync_job_status.progress_percent || 0)), // Clamp between 0-100
        stage: rawEntity.sync_job_status.stage
          ? this.validateSyncJobStage(rawEntity.sync_job_status.stage)
          : 'bills',
        startedAt: rawEntity.sync_job_status.started_at || '',
        errorDetails: rawEntity.sync_job_status.error_details,
        durationSeconds: rawEntity.sync_job_status.duration_seconds
      } : undefined
    };
  }

  /**
   * Transform raw backend tag response to frontend TagResponse
   */
  private static transformRawTag(rawTag: Record<string, unknown>): TagResponse {
    return {
      tagId: (rawTag.tag_id || rawTag.tagId) as string,
      name: (rawTag.name || '') as string,
      color: (rawTag.color || '#6b7280') as string,
      description: rawTag.description as string | undefined,
      usageCount: (rawTag.usage_count || rawTag.usageCount || 0) as number,
      createdAt: (rawTag.created_at || rawTag.createdAt || '') as string,
      updatedAt: (rawTag.updated_at || rawTag.updatedAt || '') as string,
      createdBy: (rawTag.created_by || rawTag.createdBy || '') as string,
    };
  }

  /**
   * Transform raw backend tag list response to frontend TagListResponse
   */
  private static transformTagListResponse(rawResponse: Record<string, unknown>): TagListResponse {
    return {
      tags: Array.isArray(rawResponse.tags) ? rawResponse.tags.map(tag => this.transformRawTag(tag)) : [],
      total: (rawResponse.total || 0) as number,
      pagination: (rawResponse.pagination || {
        current_page: 1,
        page_size: 50,
        total_items: 0,
        total_pages: 0,
      }) as PaginationInfo,
    };
  }

  /**
   * Transform raw backend tag delete response to frontend TagDeleteResponse
   */
  private static transformTagDeleteResponse(rawResponse: Record<string, unknown>): TagDeleteResponse {
    return {
      message: rawResponse.message as string,
      affectedEntities: rawResponse.affected_entities as number,
    };
  }

  /**
   * Transform raw backend bulk update response to frontend TagBulkUpdateResponse
   */
  private static transformTagBulkUpdateResponse(rawResponse: Record<string, unknown>): TagBulkUpdateResponse {
    return {
      message: rawResponse.message as string,
      updatedEntities: rawResponse.updated_entities as number,
      errors: Array.isArray(rawResponse.errors) ? rawResponse.errors.map((error: Record<string, unknown>) => ({
        entityId: error.entity_id as string,
        error: error.error as string,
      })) : [],
    };
  }

  /**
   * Get entities for a specific client with enhanced filtering
   */
  static async getEntitiesForClient(clientId: string, filters?: EntityFilters): Promise<EntityListResponse> {
    try {
      const params = new URLSearchParams();
      // client_id is now in the path, no longer needed in params for this specific endpoint
      // params.append('client_id', clientId); 

      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.type) params.append('type', filters.type);
      // Ensure backend for /clients/{clientId}/entities supports these or remove if not
      if (filters?.status) params.append('status', filters.status); 
      if (filters?.connectionStatus) params.append('connection_status', filters.connectionStatus);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.sortBy) params.append('sort_by', filters.sortBy);
      if (filters?.sortOrder) params.append('sort_order', filters.sortOrder);
      
      // Tag-specific filters - backend expects include_tag_details (snake_case)
      if (filters?.includeTagDetails !== undefined) {
        params.append('include_tag_details', filters.includeTagDetails.toString());
      }

      const queryString = params.toString();
      const endpoint = `/clients/${clientId}/entities${queryString ? '?' + queryString : ''}`;
      
      // Expecting the backend to return something like RawBackendResponse
      const response = await api.get<RawBackendResponse>(endpoint);
      
      // Backend response received successfully
      
      const rawEntities = response.entities || [];
      const transformedEntities: EntitySummary[] = rawEntities.map(rawEntity =>
        this.transformRawEntity(rawEntity)
      );
      
      // Assuming the backend for /clients/{client_id}/entities might not have pagination
      // matching EntityListResponse. Adjust as needed.
      // If the /clients/{clientId}/entities endpoint doesn't return pagination, construct it or adapt.
      return {
        entities: transformedEntities,
        // pagination: response.pagination // Or handle if backend's pagination structure differs
        // For now, if /clients/{clientId}/entities doesn't send pagination, this will be undefined.
        // The component might need to handle undefined pagination.
        // Or, if total is sent:
         pagination: response.total !== undefined ? {
            current_page: filters?.page || 1,
            page_size: filters?.limit || rawEntities.length, // Or a default
            total_items: response.total,
            total_pages: filters?.limit ? Math.ceil(response.total / filters.limit) : 1,
         } : undefined,
         // Pass through credit_info if available
         credit_info: response.credit_info,
      };

    } catch (error) {
      console.error('Error fetching entities for client:', error);
      throw new Error('Failed to fetch entities');
    }
  }

  /**
   * Get entity details including settings and health metrics
   */
  static async getEntity(entityId: string, disableCache: boolean = false): Promise<EntityResponse> {
    const cacheKey = `entity-${entityId}-${disableCache}`;

    return this.getOrCreateRequest(cacheKey, async (signal) => {
      try {
        return await api.get<EntityResponse>(`/entities/${entityId}`, {
          cache: !disableCache,
          signal
        });
      } catch (error) {
        // Enhanced error handling
        if (signal.aborted) {
          throw new Error('Request cancelled');
        }

        console.error(`Error fetching entity ${entityId}:`, error);
        throw new Error(`Failed to fetch entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });
  }

  /**
   * Create a new entity using wizard data
   */
  static async createEntityFromWizard(
    step1: EntityWizardStep1,
    step2: EntityWizardStep2,
    step3: EntityWizardStep3,
    clientId: string
  ): Promise<string> {
    try {
      const entityData: EntityCreate = {
        client_id: clientId,
        entity_name: step1.entity_name,
        type: step1.type,
        description: step1.description,
        settings: {
          ...step3.settings,
          auto_sync_enabled: step3.auto_sync_enabled,
          sync_frequency: step3.sync_frequency
        }
      };

      const response = await api.post<CreateEntityResponse>('/entities', entityData);

      // If OAuth connection is required, initiate it
      if (step2.connection_method === 'oauth' && step2.oauth_provider) {
        await this.initiateOAuthConnection(response.entity_id, step2.oauth_provider);
      }

      return response.entity_id;
    } catch (error) {
      console.error('Error creating entity:', error);
      throw new Error('Failed to create entity');
    }
  }

  /**
   * Update entity details
   */
  static async updateEntity(entityId: string, updates: EntityUpdate): Promise<UpdateEntityResponse | void> {
    try {
      const response = await api.put<UpdateEntityResponse>(`/entities/${entityId}`, updates);
      return response;
    } catch (error: unknown) {
      console.error('Error updating entity:', error);
      const axiosError = error as { response?: { data: unknown } };
      console.error('Error response data:', axiosError.response?.data);
      
      // Re-throw the error with the original response data for better error handling
      if (axiosError.response) {
        throw error; // Preserve the axios error with response data
      }
      throw new Error('Failed to update entity');
    }
  }

  /**
   * Delete an entity
   */
  static async deleteEntity(entityId: string): Promise<void> {
    try {
      await api.delete<DeleteEntityResponse>(`/entities/${entityId}`);
    } catch (error) {
      console.error('Error deleting entity:', error);
      throw new Error('Failed to delete entity');
    }
  }

  /**
   * Update entity settings
   */
  static async updateEntitySettings(
    entityId: string,
    settings: {
      prepayment_asset_account_codes?: string[];
      excluded_pnl_account_codes?: string[];
      default_expense_account_code?: string;
      default_amortization_months?: number;
      amortization_materiality_threshold?: number;
      amortization_apply_50_percent_rule?: boolean;
      entity_name?: string;
      auto_sync_enabled?: boolean;
      sync_frequency?: 'hourly' | 'daily' | 'weekly' | 'manual';
      sync_spend_money?: boolean;
      // Transaction sync settings
      transaction_sync_start_date?: string;
      // Individual sync data type toggles - DEPRECATED: System now automatically syncs all data types
      sync_invoices?: boolean;
      sync_bills?: boolean;
      sync_payments?: boolean;
      sync_bank_transactions?: boolean;
      sync_journal_entries?: boolean;
      // Additional settings
      auto_post_proposed_journals?: boolean;
      enable_ai_scanning?: boolean; // AI scanning toggle (maps to enable_llm_prepayment_detection in DB)
      universal_ocr_enabled?: boolean; // Phase 1: OCR all attachments (not just prepayments)
      // base_currency_code removed - should not be editable, comes from accounting system
      initial_sync_completed?: boolean;
      last_full_sync_date?: string;
      notification_preferences?: {
        sync_errors: boolean;
        connection_issues: boolean;
        data_anomalies: boolean;
      };
      // Compliance thresholds (backend uses snake_case)
      compliance_thresholds?: {
        aged_payables_days?: number;
        aged_receivables_days?: number;
        serial_checker_max_gap?: number;
        serial_checker_inversion_limit?: number;
        low_cost_asset_threshold?: number;
        old_credit_days?: number;  // Used for both Type L and M
        misallocation_threshold?: number;
        misallocation_accounts?: string[];
      };
      // AML thresholds (backend uses snake_case)
      aml_thresholds?: {
        enabled?: boolean;
        new_supplier_threshold?: number;
        new_customer_threshold?: number;
        count_spike_multiplier?: number;
        volume_spike_multiplier?: number;
        soft_gl_amount_threshold?: number;
        attachment_threshold?: number;
        cn_ratio_threshold?: number;
        round_number_threshold?: number;
        backdate_days_threshold?: number;
        pass_through_threshold?: number;
        claim_duration_hours?: number;
        max_snooze_days?: number;
        max_resubmits?: number;
      };
    }
  ): Promise<void> {
    try {
      await api.put(`/entities/${entityId}/settings`, settings);
    } catch (error) {
      console.error('Error updating entity settings:', error);
      throw new Error('Failed to update entity settings');
    }
  }

  /**
   * Get entity dashboard data with health metrics
   */
  static async getEntityDashboard(entityId: string): Promise<EntityDashboardData> {
    try {
      return await api.get<EntityDashboardData>(`/entities/${entityId}/dashboard`);
    } catch (error) {
      console.error('Error fetching entity dashboard:', error);
      throw new Error('Failed to fetch entity dashboard');
    }
  }

  /**
   * Get entity health metrics
   */
  static async getEntityHealthMetrics(entityId: string): Promise<EntityHealthMetrics> {
    try {
      return await api.get<EntityHealthMetrics>(`/entities/${entityId}/health`);
    } catch (error) {
      console.error('Error fetching entity health metrics:', error);
      throw new Error('Failed to fetch entity health metrics');
    }
  }

  /**
   * Check entity connection status
   */
  static async checkConnectionStatus(entityId: string): Promise<EntityConnectionStatus> {
    try {
      return await api.get<EntityConnectionStatus>(`/entities/${entityId}/connection/status`);
    } catch (error) {
      console.error('Error checking connection status:', error);
      throw new Error('Failed to check connection status');
    }
  }

  /**
   * Test entity connection
   */
  static async testConnection(entityId: string): Promise<ConnectionTestResult> {
    try {
      return await api.post<ConnectionTestResult>(`/entities/${entityId}/connection/test`);
    } catch (error) {
      console.error('Error testing connection:', error);
      throw new Error('Failed to test connection');
    }
  }

  /**
   * Get sync status for an entity
   */
  static async getSyncStatus(entityId: string): Promise<SyncStatus> {
    const cacheKey = `sync-status-${entityId}`;

    return this.getOrCreateRequest(cacheKey, async (signal) => {
      try {
        return await api.get<SyncStatus>(`/entities/${entityId}/sync/status`, {
          signal
        });
      } catch (error) {
        if (signal.aborted) {
          throw new Error('Request cancelled');
        }

        console.error(`Error fetching sync status for entity ${entityId}:`, error);
        throw new Error(`Failed to fetch sync status: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });
  }

  /**
   * Trigger manual sync for an entity with enhanced response handling
   */
  static async triggerSync(entityId: string): Promise<SyncTriggerResponse> {
    try {
      const response = await api.post<{
        message: string;
        sync_job_id: string;
        status: string;
        progress_endpoint: string;
      }>(`/entities/${entityId}/sync/trigger`);

      // Transform snake_case response to camelCase
      return {
        message: response.message,
        syncJobId: response.sync_job_id,
        status: 'initiated',
        progressEndpoint: response.progress_endpoint
      };
    } catch (error: unknown) {
      // Handle sync conflict (409) errors with enhanced details
      const axiosError = error as { response?: { status: number; data: { error: string; message: string; sync_job_id: string; progress_percent?: number; started_at?: string; current_stage?: string; estimated_remaining?: string } }; syncConflict?: unknown };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.error === 'sync_in_progress') {
        const conflictData = axiosError.response.data;
        const syncConflictError: SyncConflictError = {
          error: conflictData.error as 'sync_in_progress',
          message: conflictData.message,
          syncJobId: conflictData.sync_job_id,
          progressPercent: conflictData.progress_percent || 0,
          startedAt: conflictData.started_at || '',
          currentStage: conflictData.current_stage || 'processing',
          estimatedRemaining: conflictData.estimated_remaining || '~5 minutes'
        };

        // Attach the parsed conflict data to the error for better handling
        axiosError.syncConflict = syncConflictError;
      }

      console.error('Error triggering sync:', error);
      throw error;
    }
  }

  /**
   * Get sync job status by job ID for targeted polling
   */
  static async getSyncJobStatus(syncJobId: string): Promise<SyncJobStatus> {
    const cacheKey = `sync-job-${syncJobId}`;

    return this.getOrCreateRequest(cacheKey, async (signal) => {
      try {
        const response = await api.get<{
          sync_job_id: string;
          entity_id: string;
          status: string; // Use string to allow for validation
          progress_percent: number;
          stage: string; // Use string to allow for validation
          started_at: string;
          error_details?: Record<string, any>;
          duration_seconds?: number;
        }>(`/sync/status/${syncJobId}`, { signal });

        // Validate response data and transform snake_case response to camelCase
        if (!response.sync_job_id || !response.entity_id) {
          throw new Error('Invalid sync job status response: missing required fields');
        }

        return {
          syncJobId: response.sync_job_id,
          entityId: response.entity_id,
          status: this.validateSyncJobStatus(response.status),
          progressPercent: Math.max(0, Math.min(100, response.progress_percent || 0)), // Clamp between 0-100
          stage: this.validateSyncJobStage(response.stage),
          startedAt: response.started_at || '',
          errorDetails: response.error_details,
          durationSeconds: response.duration_seconds
        };
      } catch (error) {
        if (signal.aborted) {
          throw new Error('Request cancelled');
        }

        console.error(`Error fetching sync job status ${syncJobId}:`, error);

        // Enhanced error handling with more specific error messages
        if (error instanceof Error) {
          throw new Error(`Failed to fetch sync job status: ${error.message}`);
        }
        throw new Error('Failed to fetch sync job status: Unknown error');
      }
    }, 15000); // Shorter timeout for sync job status (15 seconds)
  }

  /**
   * Disconnect an entity
   */
  static async disconnectEntity(entityId: string): Promise<{ message: string }> {
    try {
      const result = await api.post<{ message: string }>(`/entities/${entityId}/connection/disconnect`);
      return result;
    } catch (error) {
      console.error('EntitiesService: Error disconnecting entity:', error);

      // Re-throw with more context
      if (error instanceof Error) {
        throw new Error(`Failed to disconnect entity: ${error.message}`);
      }
      throw new Error('Failed to disconnect entity: Unknown error');
    }
  }

  /**
   * Initiate OAuth connection for an entity
   */
  static async initiateOAuthConnection(entityId: string, provider: 'xero' | 'qbo'): Promise<OAuthInitiateResponse> {
    try {
      return await api.post<OAuthInitiateResponse>(`/entities/${entityId}/oauth/initiate`, { provider });
    } catch (error) {
      console.error('Error initiating OAuth connection:', error);
      throw new Error('Failed to initiate OAuth connection');
    }
  }

  /**
   * Complete OAuth connection with callback data
   */
  static async completeOAuthConnection(entityId: string, callbackData: OAuthCallbackData): Promise<CompleteConnectionResponse> {
    try {
      return await api.post<CompleteConnectionResponse>(`/entities/${entityId}/oauth/callback`, callbackData);
    } catch (error) {
      console.error('Error completing OAuth connection:', error);
      throw new Error('Failed to complete OAuth connection');
    }
  }

  /**
   * Initiate Xero connection for a client (legacy method for backward compatibility)
   */
  static async initiateXeroConnection(clientId: string): Promise<string> {
    try {
      // Try the API client first
      try {
        const response = await api.get<{ authorization_url: string }>(`/xero/connect/initiate/${clientId}`);

        if (response && typeof response === 'object') {
          if ('authorization_url' in response) {
            return response.authorization_url;
          } else {
            console.error('EntitiesService: Missing authorization_url in response. Available keys:', Object.keys(response));
            console.error('EntitiesService: Full response:', JSON.stringify(response, null, 2));
            throw new Error(`Invalid response format: missing authorization_url. Available keys: ${Object.keys(response).join(', ')}`);
          }
        } else {
          console.error('EntitiesService: Invalid response format - not an object:', response);
          throw new Error(`Invalid response format: expected object, got ${typeof response}`);
        }
      } catch (apiError) {
        console.error('EntitiesService: API client failed, trying direct axios call:', apiError);

        // Fallback to direct axios call
        const { auth } = await import('@/lib/firebase');
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated');
        }

        const token = await user.getIdToken();
        const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

        const axios = (await import('axios')).default;
        const directResponse = await axios.get(`${baseURL}/xero/connect/initiate/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        });

        if (directResponse.data && typeof directResponse.data === 'object' && 'authorization_url' in directResponse.data) {
          return directResponse.data.authorization_url;
        } else {
          throw new Error('Direct axios call also failed - invalid response format');
        }
      }
    } catch (error) {
      console.error('EntitiesService: Error initiating Xero connection:', error);

      // Log more details about the error
      if (error && typeof error === 'object') {
        const axiosError = error as { message?: string; response?: { data: unknown; status: number; statusText: string } };
        console.error('EntitiesService: Error details:', {
          message: axiosError.message,
          response: axiosError.response?.data,
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText
        });
      }

      // Re-throw the original error if it has a specific message, otherwise use generic
      if (error instanceof Error && error.message.includes('Invalid response format')) {
        throw error;
      }

      throw new Error('Failed to initiate Xero connection');
    }
  }

  /**
   * Get chart of accounts for a Xero entity (from Firebase cache first)
   */
  static async getChartOfAccounts(entityId: string): Promise<Account[]> {
    try {
      // Use the entities endpoint which reads from Firebase cache first, then falls back to Xero
      const response = await api.get<{ accounts: Account[] }>(`/entities/${entityId}/accounts`);
      return response.accounts;
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      throw new Error('Failed to fetch chart of accounts');
    }
  }

  /**
   * Get tax rates for an entity
   */
  static async getTaxRates(entityId: string, signal?: AbortSignal): Promise<TaxRate[]> {
    try {
      const response = await api.get<{ tax_rates: TaxRate[] }>(`/entities/${entityId}/tax-rates`, { signal });
      return response.tax_rates;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching tax rates:', error);
      throw new Error('Failed to fetch tax rates');
    }
  }

  /**
   * Revoke OAuth connection
   */
  static async revokeOAuthConnection(entityId: string): Promise<void> {
    try {
      await api.post(`/entities/${entityId}/oauth/revoke`);
    } catch (error) {
      console.error('Error revoking OAuth connection:', error);
      throw new Error('Failed to revoke OAuth connection');
    }
  }

  /**
   * Get entity enums for UI dropdowns
   */
  static async getEntityEnums(): Promise<EntityEnums> {
    try {
      return await api.get<EntityEnums>('/entities/enums');
    } catch (error) {
      console.error('Error fetching entity enums:', error);
      // Return default enums if API fails
      const defaultEnums: EntityEnums = {
        entity_types: [
          { value: 'xero', label: 'Xero', description: 'Xero accounting software' },
          { value: 'qbo', label: 'QuickBooks Online', description: 'QuickBooks Online accounting software' },
          { value: 'manual', label: 'Manual Entry', description: 'Manual data entry' }
        ],
        entity_statuses: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'error', label: 'Error' },
          { value: 'syncing', label: 'Syncing' },
          { value: 'pending', label: 'Pending' },
          { value: 'disconnected', label: 'Disconnected' }
        ],
        sync_frequencies: [
          { value: 'hourly', label: 'Hourly' },
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'manual', label: 'Manual' }
        ],
        currencies: [
          { value: 'USD', label: 'US Dollar', symbol: '$' },
          { value: 'EUR', label: 'Euro', symbol: '€' },
          { value: 'GBP', label: 'British Pound', symbol: '£' },
          { value: 'CAD', label: 'Canadian Dollar', symbol: 'C$' },
          { value: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
          { value: 'NZD', label: 'New Zealand Dollar', symbol: 'NZ$' },
          { value: 'JPY', label: 'Japanese Yen', symbol: '¥' },
          { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' }
        ],
        amortization_periods: [
          { value: 1, label: '1 Month' },
          { value: 3, label: '3 Months' },
          { value: 6, label: '6 Months' },
          { value: 12, label: '12 Months' },
          { value: 24, label: '24 Months' },
          { value: 36, label: '36 Months' }
        ],
        sync_data_types: [
          { value: 'invoices', label: 'Invoices', description: 'Customer invoices and sales' },
          { value: 'bills', label: 'Bills', description: 'Vendor bills and purchases' },
          { value: 'payments', label: 'Payments', description: 'Payment transactions' },
          { value: 'bank_transactions', label: 'Bank Transactions', description: 'Bank account transactions' },
          { value: 'journal_entries', label: 'Journal Entries', description: 'Manual journal entries' },
          { value: 'spend_money', label: 'Spend Money / Expenses', description: 'Spend Money transactions (Xero) / Expenses (QuickBooks Online)' }
        ]
      };
      return defaultEnums;
    }
  }

  /**
   * Get contacts for an entity with pagination and filtering
   */
  static async getContacts(entityId: string, filters?: ContactFilters): Promise<ContactListResponse> {
    try {
      const result = await api.getContacts(entityId, filters);
      return result;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw new Error('Failed to fetch contacts');
    }
  }

  /**
   * Get detailed contact information by ID
   */
  static async getContact(contactId: string): Promise<ContactDetailResponse> {
    try {
      return await api.get<ContactDetailResponse>(`/contacts/${contactId}`);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      throw new Error('Failed to fetch contact details');
    }
  }

  /**
   * Get audit logs for an entity with pagination and filtering
   */
  static async getAuditLogs(entityId: string, filters?: AuditLogFilters): Promise<AuditLogListResponse> {
    try {
      return await api.getAuditLogs(entityId, filters);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw new Error('Failed to fetch audit logs');
    }
  }

  /**
   * Get detailed audit log information by ID
   */
  static async getAuditLog(auditId: string, includeRelated: boolean = true): Promise<AuditLogDetailResponse> {
    try {
      const params = new URLSearchParams();
      if (includeRelated) params.append('include_related', 'true');
      
      const queryString = params.toString();
      const endpoint = `/audit/${auditId}${queryString ? '?' + queryString : ''}`;
      
      return await api.get<AuditLogDetailResponse>(endpoint);
    } catch (error) {
      console.error('Error fetching audit log details:', error);
      throw new Error('Failed to fetch audit log details');
    }
  }

  /**
   * Get audit statistics for an entity
   */
  static async getAuditStats(entityId: string, dateFrom?: string, dateTo?: string): Promise<AuditLogStatsResponse> {
    try {
      const params = new URLSearchParams();
      params.append('entity_id', entityId);
      
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const queryString = params.toString();
      const endpoint = `/audit/stats/summary${queryString ? '?' + queryString : ''}`;
      
      return await api.get<AuditLogStatsResponse>(endpoint);
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      throw new Error('Failed to fetch audit statistics');
    }
  }

  // =================================================================
  // TAG MANAGEMENT METHODS
  // =================================================================

  /**
   * Get all tags for the current user's firm
   */
  static async getFirmTags(filters?: TagFilters): Promise<TagListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.includeUnused !== undefined) {
        params.append('include_unused', filters.includeUnused.toString());
      }
      if (filters?.sortBy) params.append('sort_by', filters.sortBy);
      if (filters?.sortOrder) params.append('sort_order', filters.sortOrder);

      const queryString = params.toString();
      const endpoint = `/entities/tags${queryString ? '?' + queryString : ''}`;

      const response = await api.get<Record<string, unknown>>(endpoint);
      return this.transformTagListResponse(response);
    } catch (error) {
      console.error('Error fetching firm tags:', error);
      if (error instanceof Error) {
        throw error; // Re-throw the original error with its message
      }
      throw new Error('Failed to fetch tags');
    }
  }

  /**
   * Create a new tag for the current user's firm
   */
  static async createTag(request: TagCreateRequest): Promise<TagResponse> {
    try {
      const response = await api.post<Record<string, unknown>>('/entities/tags', request);
      return this.transformRawTag(response);
    } catch (error) {
      console.error('Error creating tag:', error);
      throw new Error('Failed to create tag');
    }
  }

  /**
   * Update an existing tag
   */
  static async updateTag(tagId: string, request: TagUpdateRequest): Promise<TagResponse> {
    try {
      const response = await api.put<Record<string, unknown>>(`/entities/tags/${tagId}`, request);
      return this.transformRawTag(response);
    } catch (error) {
      console.error('Error updating tag:', error);
      throw new Error('Failed to update tag');
    }
  }

  /**
   * Delete a tag and remove it from all entities
   */
  static async deleteTag(tagId: string): Promise<TagDeleteResponse> {
    try {
      const response = await api.delete<Record<string, unknown>>(`/entities/tags/${tagId}`);
      return this.transformTagDeleteResponse(response);
    } catch (error) {
      console.error('Error deleting tag:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete tag';
      throw new Error(errorMessage);
    }
  }


  /**
   * Bulk update tags for multiple entities
   */
  static async bulkUpdateEntityTags(request: TagBulkUpdateRequest): Promise<TagBulkUpdateResponse> {
    try {
      // Transform camelCase request to snake_case for backend
      const backendRequest = {
        entity_ids: request.entityIds,
        add_tag_ids: request.addTagIds,
        remove_tag_ids: request.removeTagIds,
      };
      
      const response = await api.post<Record<string, unknown>>('/entities/tags/bulk-update', backendRequest);
      return this.transformTagBulkUpdateResponse(response);
    } catch (error) {
      console.error('Error bulk updating entity tags:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to bulk update tags';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get entities filtered by tags
   */
  static async getEntitiesWithTags(clientId: string, filters?: EntityFilters): Promise<EntityListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.type) params.append('type', filters.type);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.connectionStatus) params.append('connection_status', filters.connectionStatus);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.sortBy) params.append('sort_by', filters.sortBy);
      if (filters?.sortOrder) params.append('sort_order', filters.sortOrder);
      
      // Tag-specific filters
      if (filters?.tagIds && Array.isArray(filters.tagIds) && filters.tagIds.length > 0) {
        filters.tagIds
          .filter(tagId => tagId && typeof tagId === 'string')
          .forEach(tagId => params.append('tag_ids', tagId));
      }
      if (filters?.includeTagDetails !== undefined) {
        params.append('include_tag_details', filters.includeTagDetails.toString());
      }

      const queryString = params.toString();
      const endpoint = `/clients/${clientId}/entities${queryString ? '?' + queryString : ''}`;
      
      // This uses the same endpoint as getEntitiesForClient but with tag filtering
      const response = await api.get<RawBackendResponse>(endpoint);
      
      // Transform the response with tag support
      const rawEntities = response.entities || [];
      const transformedEntities: EntitySummary[] = rawEntities.map(rawEntity => 
        this.transformRawEntity(rawEntity)
      );
      
      return {
        entities: transformedEntities,
        pagination: response.total !== undefined ? {
          current_page: filters?.page || 1,
          page_size: filters?.limit || rawEntities.length,
          total_items: response.total,
          total_pages: filters?.limit ? Math.ceil(response.total / filters.limit) : 1,
        } : undefined,
        credit_info: response.credit_info,
      };

    } catch (error) {
      console.error('Error fetching entities with tags:', error);
      throw new Error('Failed to fetch entities with tags');
    }
  }

  /**
   * Get entity with full tag details
   */
  static async getEntityWithTags(entityId: string): Promise<EntityResponse> {
    try {
      const params = new URLSearchParams();
      params.append('include_tag_details', 'true');

      const queryString = params.toString();
      const endpoint = `/entities/${entityId}${queryString ? '?' + queryString : ''}`;

      return await api.get<EntityResponse>(endpoint);
    } catch (error) {
      console.error('Error fetching entity with tags:', error);
      throw new Error('Failed to fetch entity with tags');
    }
  }

  // ==========================================================================
  // ONBOARDING STATE METHODS
  // ==========================================================================

  /**
   * Get onboarding checklist state for an entity
   */
  static async getOnboardingState(entityId: string): Promise<{
    onboarding_completed: boolean;
    checklist_dismissed: boolean;
    steps_completed: {
      xero_connected: boolean;
      sync_completed: boolean;
      compliance_reviewed: boolean;
      prepayments_configured: boolean;
      aml_configured: boolean;
      thresholds_customized: boolean;
    };
  }> {
    type OnboardingStateResponse = {
      onboarding_completed: boolean;
      checklist_dismissed: boolean;
      steps_completed: {
        xero_connected: boolean;
        sync_completed: boolean;
        compliance_reviewed: boolean;
        prepayments_configured: boolean;
        aml_configured: boolean;
        thresholds_customized: boolean;
      };
    };
    try {
      const response = await api.get<OnboardingStateResponse>(`/entities/${entityId}/onboarding-state`);
      return response;
    } catch (error) {
      console.error('Failed to get onboarding state:', error);
      throw error;
    }
  }

  /**
   * Update a step in the onboarding checklist
   */
  static async updateOnboardingStep(
    entityId: string,
    step: string,
    completed: boolean
  ): Promise<{
    onboarding_completed: boolean;
    checklist_dismissed: boolean;
    steps_completed: Record<string, boolean>;
  }> {
    type UpdateStepResponse = {
      onboarding_completed: boolean;
      checklist_dismissed: boolean;
      steps_completed: Record<string, boolean>;
    };
    try {
      const response = await api.put<UpdateStepResponse>(`/entities/${entityId}/onboarding-state`, {
        step,
        completed
      });
      return response;
    } catch (error) {
      console.error('Failed to update onboarding step:', error);
      throw error;
    }
  }

  /**
   * Dismiss the onboarding checklist
   */
  static async dismissOnboarding(entityId: string): Promise<{ message: string }> {
    try {
      const response = await api.post<{ message: string }>(`/entities/${entityId}/onboarding/dismiss`);
      return response;
    } catch (error) {
      console.error('Failed to dismiss onboarding:', error);
      throw error;
    }
  }

  /**
   * Cleanup utility - cancel all pending requests and clear caches
   * Useful for component unmount or service reset
   */
  static cleanup(): void {
    this.cancelAllRequests();
    console.debug('EntitiesService cleanup completed');
  }

  /**
   * Get statistics about current request state (for debugging/monitoring)
   */
  static getRequestStats(): {
    pendingRequests: number;
    activeControllers: number;
    cacheKeys: string[];
  } {
    return {
      pendingRequests: this.requestCache.size,
      activeControllers: this.abortControllers.size,
      cacheKeys: Array.from(this.requestCache.keys())
    };
  }
}
